import { Box, CircularProgress, Typography } from "@mui/material";
import { fmtDias } from "../utils/format";
import {
  TE_CARD_SX,
  TE_COLORS,
  TE_PANEL_DESC_SX,
  TE_PANEL_TITLE_SX,
} from "./theme";

export interface RankingItem {
  nome: string;
  media: number;
  count: number;
}

interface RankingPanelProps {
  title: string;
  description: string;
  items: RankingItem[];
  emptyMessage: string;
  loading?: boolean;
}

function threshColor(ratio: number): string {
  if (ratio > 0.66) return TE_COLORS.red;
  if (ratio > 0.33) return TE_COLORS.amber;
  return TE_COLORS.green;
}

export function RankingPanel({
  title,
  description,
  items,
  emptyMessage,
  loading,
}: RankingPanelProps) {
  const max = Math.max(1, ...items.map((i) => i.media));

  return (
    <Box sx={TE_CARD_SX}>
      <Typography sx={{ ...TE_PANEL_TITLE_SX, mb: 0.5 }}>{title}</Typography>
      <Typography sx={{ ...TE_PANEL_DESC_SX, mb: 1.5 }}>
        {description}
      </Typography>

      {loading ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ py: 5 }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : !items.length ? (
        <Typography
          sx={{
            color: TE_COLORS.textFaint,
            fontSize: 12.5,
            py: 2.5,
            textAlign: "center",
          }}
        >
          {emptyMessage}
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column">
          {items.map((item) => {
            const pct = (item.media / max) * 100;
            return (
              <Box
                key={item.nome}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "170px 1fr 90px",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1,
                  borderBottom: "1px solid #F4F5F9",
                  "&:last-of-type": { borderBottom: "none" },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 500,
                    color: TE_COLORS.text,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.nome}
                </Typography>
                <Box
                  sx={{
                    bgcolor: "#EEF0F6",
                    height: 16,
                    borderRadius: "5px",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      borderRadius: "5px",
                      width: `${pct}%`,
                      bgcolor: threshColor(item.media / max),
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: TE_COLORS.textDim,
                    textAlign: "right",
                  }}
                >
                  {fmtDias(item.media)} · {item.count}p
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
